import { useEffect, useState } from 'react';

import { CheckboxField, Text } from '../../../common/components';
import { useAppDispatch } from '../../../common/hooks';
import { t } from '../../../common/i18n';
import { setFlashMessage } from '../../../common/store/uiSlice';
import {
  getMonitoringNotificationSetting,
  MonitoringNotificationSetting,
  setMonitoringNotificationSetting,
} from '../../services/RegistryService';

/**
 * Per-admin nastavení e-mailových notifikací monitoringu: okamžité upozornění na změnu
 * + měsíční souhrnný report. Platí jen pro přihlášeného admina (jako u Termínů);
 * odhlásit se lze i podepsaným odkazem přímo v doručeném e-mailu.
 */
export const MonitoringNotificationSettings: React.FC = () => {
  const dispatch = useAppDispatch();
  const [setting, setSetting] = useState<MonitoringNotificationSetting | null>(null);

  useEffect(() => {
    getMonitoringNotificationSetting()
      .then(setSetting)
      .catch(() => setSetting(null));
  }, []);

  if (setting === null) return null;

  const handleToggle = async (type: 'change' | 'report', enabled: boolean) => {
    const previous = setting;
    setSetting({ ...setting, [type === 'change' ? 'changeEnabled' : 'reportEnabled']: enabled });

    try {
      const updated = await setMonitoringNotificationSetting(type, enabled);
      setSetting(updated);
      dispatch(
        setFlashMessage({
          message: t(enabled ? 'Registry.notificationSettings.successOn' : 'Registry.notificationSettings.successOff'),
          type: 'success',
        })
      );
    } catch {
      setSetting(previous);
      dispatch(setFlashMessage({ message: t('Registry.notificationSettings.error'), type: 'error' }));
    }
  };

  return (
    <div className="MonitoringNotificationSettings">
      <Text size="h2" color="Neutral800" className="mb-2">
        {t('Registry.notificationSettings.title')}
      </Text>

      <CheckboxField
        name="monitoring-change-notifications"
        variant="switch"
        label={t('Registry.notificationSettings.changeLabel')}
        checked={setting.changeEnabled}
        onChange={() => handleToggle('change', !setting.changeEnabled)}
      />
      <CheckboxField
        name="monitoring-report-notifications"
        variant="switch"
        label={t('Registry.notificationSettings.reportLabel')}
        checked={setting.reportEnabled}
        onChange={() => handleToggle('report', !setting.reportEnabled)}
      />

      <Text as="div" size="body-small" color="Neutral600" className="MonitoringNotificationSettings__note">
        {t('Registry.notificationSettings.note')}
      </Text>
    </div>
  );
};

export default MonitoringNotificationSettings;
